import type { FeaturesContentSectionFragment } from '@/lib/types/datocms/graphql/generated'
import Image from 'next/image'
import { StructuredText } from 'react-datocms'
import { TextWithHighlight } from '../TextWithHighlight'
import clsx from 'clsx'
import { isStructuredTextEmpty } from '@/utils/datocms/isStructuredTextEmpty'

export default function FeaturesContentSection({
	title,
	description,
	image,
	items,
	inMegaMenu,
}: Readonly<FeaturesContentSectionFragment & { inMegaMenu?: boolean }>) {
	if (!items.length) return null
	return (
		<section className="features-content-section @container">
			<div
				className={clsx(
					'max-w-screen-2xl mx-auto ',
					!inMegaMenu && 'common-x-padding py-16 @xl:py-20'
				)}
			>
				{((title && !isStructuredTextEmpty(title.text)) ||
					(description && !isStructuredTextEmpty(description))) && (
					<div
						className={clsx(
							'flex flex-col @xl:flex-row gap-y-4 gap-x-10 @lg:gap-x-32',
							inMegaMenu ? '@xl:mb-10' : '@xl:mb-20'
						)}
					>
						{title && !isStructuredTextEmpty(title.text) && (
							<h2 className={clsx(inMegaMenu ? 'heading-size-5' : 'heading-size-3', 'md:w-1/2')}>
								<TextWithHighlight text={title} />
							</h2>
						)}
						{description && !isStructuredTextEmpty(description) && (
							<div
								className={clsx(
									'rich-text text-blue-dark/80',
									inMegaMenu
										? 'text-base [&>p]:!text-base [&>p]:leading-tight'
										: 'text-lg @3xl:w-1/2'
								)}
							>
								<StructuredText data={description} />
							</div>
						)}
					</div>
				)}

				<div
					className={clsx(
						'flex flex-col @xl:flex-row gap-y-3 gap-x-10 @5xl:gap-x-32',
						inMegaMenu ? 'mt-2 mb-5' : 'mt-12 @xl:mt-0'
					)}
				>
					{image && !inMegaMenu && (
						<div className="@xl:w-1/2 aspect-video overflow-hidden @3xl:aspect-square rounded-xl bg-white-frosted">
							<Image
								src={image?.[0]?.url}
								alt={image?.[0]?.alt ?? 'Features Content Section Image'}
								width={image?.[0]?.width}
								height={image?.[0]?.height}
								className="object-cover object-center w-full h-full"
								sizes="(max-width: 768px) 90vw, 40vw"
							/>
						</div>
					)}

					<div
						className={clsx(
							inMegaMenu
								? 'grid grid-cols-1 @lg:grid-cols-2 gap-5'
								: 'flex flex-col gap-3 @xl:w-1/2'
						)}
					>
						{items.map((item) => (
							<div
								key={item.id as string}
								className={clsx(
									'flex items-start gap-x-4',
									inMegaMenu ? 'bg-transparent' : 'bg-white-frosted p-4 rounded-lg'
								)}
							>
								{item.icon && (
									<div
										className={clsx(
											'w-20 h-20 rounded-md flex items-center justify-center shrink-0',
											inMegaMenu ? 'bg-white-frosted' : 'bg-white'
										)}
									>
										<Image
											src={item.icon.url}
											alt={item.icon.alt ?? 'Feature Icon'}
											width={item.icon.width as number}
											height={item.icon.height as number}
											className="w-8"
											sizes="32px"
										/>
									</div>
								)}
								<div>
									{item.featureTitle && (
										<h3 className="capitalize text-lg font-semibold mb-2.5 @3xl:mb-3">
											{item.featureTitle}
										</h3>
									)}
									{item.description && <p className="text-sm uppercase">{item.description}</p>}
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</section>
	)
}
